/* eslint-disable react/jsx-no-constructed-context-values */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Editor } from '@tiptap/core';
import { produce } from 'immer';
import {
	createContext,
	Fragment,
	useCallback,
	useContext,
	useEffect,
	useReducer,
	useRef,
	useState,
} from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { showErrorToast, showSuccessToast } from '@/components/ToastNotification';
import GlobalContext from '@/context/GlobalContext';
import { EditorType, SetStatus } from '@/Enums/enum';
import {
	existingSub,
	setDraftFrag,
	updateDraftFrag,
	updateLessonPlans,
	updateSubStatusFrag,
} from '@/hooks/mutations/courseMutations';
import { useGetAllAdmin } from '@/hooks/queries/useAdminUsers';
import { useGetCurrentCourses } from '@/hooks/queries/useCurrentCourse';
import { useCurrentUser, useUserRole } from '@/hooks/useAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { useTabLabels } from '@/hooks/useTabLabels';
import { supabase } from '@/lib/supabase';
import { useCourseLessonsStore } from '@/stores/lessonStore';
import { createEmailSubmissionLink } from '@/utils';

async function sendCourseNotificationEmail(
	accessToken: string,
	payload:
		| {
				type: 'course_status_update';
				courseName: string;
				courseUrl: string;
				recipients: string[];
		  }
		| {
				type: 'course_submission';
				courseName: string;
				courseUrl: string;
				submitterName: string;
				recipients: string[];
		  }
) {
	const response = await fetch(
		`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-course-notification`,
		{
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify(payload),
		}
	);
	if (!response.ok) {
		const errBody = (await response.json().catch(() => ({}))) as { error?: string };
		throw new Error(errBody.error ?? 'Course notification email failed');
	}
}

// Query key must match useCoursesAll so list refreshes after save/submit
const COURSES_LIST_QUERY_KEY = ['get-courses-all', import.meta.env.VITE_SCHOOL_ID];

interface ICourseEditContext {
	addFocus(): void;
	addFocusWithTitle(title: string): void;
	importFocusFromPull(payload: PulledFocusImport): void;
	addOutcome(): void;
	addOutcomeWithText(text: string): void;
	addResource(a: any): void;
	addResourceFromSuggestion(payload: {
		resourceType: string;
		title: string;
		author?: string;
		detail?: string;
		isbn?: string;
	}): void;
	addSkill(a: string, b: string): void;
	canEdit: boolean;
	courseData: Course;
	courseDraftData: Course;
	currentUserData: any;
	editorRefs: React.MutableRefObject<Map<string, Editor>>;
	isAlignment: boolean;
	isApprovalMode: boolean;
	isFetched: boolean;
	isFetchedAfterMount: boolean;
	isPendingApproval(): boolean;
	lessonPlans: any;
	removeFocus(a: any): void;
	removeOutcome(a: any): void;
	removeResource(a: any): void;
	removeSkill(a: any): void;
	saveDraft(): Promise<void>;
	submitCourse(): void;
	updateAdditionalInfo(a: any): void;
	updateCourseReducer(a: any, b: any): void;
	updateDescription(a: any): void;
	updateDraftStatus(a: any, b?: any): void;
	updateEditorRef(key: string, editor: Editor): void;
	updateFocus(a: any): void;
	updateFocusOrder(focuses: CourseFocusEntity[]): void;
	updateOutcome(a: any): void;
	updateOutcomeOrder(outcomeArr: CoursesOutcomesEntity[]): void;
	updatePrereq(a: any): void;
	updateResourceInfo(a: any): void;
	updateResources(a: any): void;
	updatePlans(plan: LessonSection[], courseId: string): void;
}

interface CourseUrlParams {
	[key: string]: string;
}

const CourseEditContext = createContext({} as ICourseEditContext);

/** Payload for pulling a unit of focus (title + optional skills) from another course. */
export type PulledFocusImport = {
	title: string;
	skills: Array<{ skill_type: string; skill_text: string }>;
};

type FocusAction =
	| { type: 'ADD_FOCUS' }
	| { type: 'ADD_FOCUS_WITH_TITLE'; payload: { title: string } }
	| { type: 'IMPORT_FOCUS_WITH_SKILLS'; payload: PulledFocusImport }
	| { type: 'ADD_SKILL'; payload: { focusId: string; skillType: string } }
	| { type: 'UPDATE_FOCUS'; payload: { courses_focuses: CourseFocusEntity[] } }
	| { type: 'UPDATE_SKILL'; payload: { focusId: string; skillId: string; text: string } }
	| { type: 'REMOVE_FOCUS'; payload: { focusId: string } }
	| { type: 'REMOVE_SKILL'; payload: { focusSkillId: string; courseFocusId: string } }
	| { type: 'REORDER_FOCUSES'; payload: { focuses: CourseFocusEntity[] } }
	| { type: 'SET_FOCUSES'; payload: { courses_focuses: CourseFocusEntity[] } };

function focusReducer(state: Course, action: FocusAction): Course {
	switch (action.type) {
		case 'SET_FOCUSES':
			return produce(state, (draft) => {
				draft.courses_focuses = action.payload.courses_focuses;
			});

		case 'ADD_FOCUS': {
			const maxOrder =
				state.courses_focuses?.reduce((max, focus) => Math.max(max, focus.focus.focus_order), -1) ??
				-1;

			return produce(state, (draft) => {
				if (!draft.courses_focuses) draft.courses_focuses = [];
				draft.courses_focuses.push({
					courses_focuses_id: uuidv4(),
					focus: {
						focus_id: uuidv4(),
						focus_order: maxOrder + 1,
						focus_type: '',
						focus_title: '',
						focuses_skills: [],
					},
				});
			});
		}

		case 'ADD_FOCUS_WITH_TITLE': {
			const maxOrderForTitle =
				state.courses_focuses?.reduce((max, focus) => Math.max(max, focus.focus.focus_order), -1) ??
				-1;

			return produce(state, (draft) => {
				if (!draft.courses_focuses) draft.courses_focuses = [];
				draft.courses_focuses.push({
					courses_focuses_id: uuidv4(),
					focus: {
						focus_id: uuidv4(),
						focus_order: maxOrderForTitle + 1,
						focus_type: '',
						focus_title: action.payload.title,
						focuses_skills: [],
					},
				});
			});
		}

		case 'IMPORT_FOCUS_WITH_SKILLS': {
			const { title, skills } = action.payload;
			const maxOrderImport =
				state.courses_focuses?.reduce((max, focus) => Math.max(max, focus.focus.focus_order), -1) ??
				-1;

			return produce(state, (draft) => {
				if (!draft.courses_focuses) draft.courses_focuses = [];
				const focuses_skills = skills.map((s, idx) => ({
					focuses_skills_id: uuidv4(),
					skill_order: idx,
					skill: {
						skill_id: uuidv4(),
						skill_text: s.skill_text,
						skill_type: s.skill_type,
					},
				}));
				draft.courses_focuses.push({
					courses_focuses_id: uuidv4(),
					focus: {
						focus_id: uuidv4(),
						focus_order: maxOrderImport + 1,
						focus_type: '',
						focus_title: title,
						focuses_skills,
					},
				});
			});
		}

		case 'ADD_SKILL': {
			const { focusId, skillType } = action.payload;
			return produce(state, (draft) => {
				const focus = draft.courses_focuses?.find((f) => f.courses_focuses_id === focusId);
				if (focus) {
					if (!focus.focus.focuses_skills) focus.focus.focuses_skills = [];
					focus.focus.focuses_skills.push({
						focuses_skills_id: uuidv4(),
						skill: {
							skill_id: uuidv4(),
							skill_text: '',
							skill_type: skillType,
						},
					});
				}
			});
		}

		case 'REMOVE_SKILL': {
			const { focusSkillId, courseFocusId } = action.payload;
			return produce(state, (draft) => {
				const focus = draft.courses_focuses?.find((f) => f.courses_focuses_id === courseFocusId);
				if (focus?.focus.focuses_skills) {
					focus.focus.focuses_skills = focus.focus.focuses_skills.filter(
						(skill) => skill.focuses_skills_id !== focusSkillId
					);
				}
			});
		}

		case 'REMOVE_FOCUS': {
			return produce(state, (draft) => {
				if (draft.courses_focuses) {
					draft.courses_focuses = draft.courses_focuses.filter(
						(focus) => focus.courses_focuses_id !== action.payload.focusId
					);
				}
			});
		}

		case 'UPDATE_FOCUS': {
			// Update focuses by ID, preserving existing order
			return produce(state, (draft) => {
				if (!draft.courses_focuses) return;

				for (const newFocus of action.payload.courses_focuses) {
					const existingIndex = draft.courses_focuses.findIndex(
						(f) => f.courses_focuses_id === newFocus.courses_focuses_id
					);
					if (existingIndex !== -1) {
						// Preserve focus_order from existing state
						const existingOrder = draft.courses_focuses[existingIndex].focus.focus_order;
						draft.courses_focuses[existingIndex] = {
							...newFocus,
							focus: {
								...newFocus.focus,
								focus_order: existingOrder,
							},
						};
					}
				}
			});
		}

		case 'REORDER_FOCUSES': {
			return produce(state, (draft) => {
				draft.courses_focuses = action.payload.focuses;
			});
		}

		default:
			return state;
	}
}

type OutcomeAction =
	| { type: 'ADD_OUTCOME' }
	| { type: 'ADD_OUTCOME_WITH_TEXT'; payload: { text: string } }
	| { type: 'UPDATE_OUTCOME'; payload: { id: string; debouncedText: string } }
	| { type: 'SET_OUTCOMES'; payload: { courses_outcomes: CoursesOutcomesEntity[] } }
	| { type: 'REMOVE_OUTCOME'; payload: { id: string } }
	| { type: 'REORDER_OUTCOMES'; payload: { outcomes: CoursesOutcomesEntity[] } };

function outcomeReducer(state: Course, action: OutcomeAction): Course {
	switch (action.type) {
		case 'SET_OUTCOMES':
			return produce(state, (draft) => {
				draft.courses_outcomes = action.payload.courses_outcomes;
			});

		case 'ADD_OUTCOME':
			return produce(state, (draft) => {
				if (!draft.courses_outcomes) draft.courses_outcomes = [];
				draft.courses_outcomes.push({
					courses_outcomes_id: uuidv4(),
					outcome: {
						outcome_id: uuidv4(),
						outcome_order: draft.courses_outcomes.length,
						outcome_text: '',
					},
				});
			});

		case 'ADD_OUTCOME_WITH_TEXT':
			return produce(state, (draft) => {
				if (!draft.courses_outcomes) draft.courses_outcomes = [];
				draft.courses_outcomes.push({
					courses_outcomes_id: uuidv4(),
					outcome: {
						outcome_id: uuidv4(),
						outcome_order: draft.courses_outcomes.length,
						outcome_text: action.payload.text,
					},
				});
			});

		case 'UPDATE_OUTCOME':
			return produce(state, (draft) => {
				const outcome = draft.courses_outcomes?.find(
					(o) => o.outcome.outcome_id === action.payload.id
				);
				if (outcome) {
					outcome.outcome.outcome_text = action.payload.debouncedText;
				}
			});

		case 'REMOVE_OUTCOME':
			return produce(state, (draft) => {
				if (draft.courses_outcomes) {
					draft.courses_outcomes = draft.courses_outcomes.filter(
						(outcome) => outcome.courses_outcomes_id !== action.payload.id
					);
				}
			});

		case 'REORDER_OUTCOMES':
			return produce(state, (draft) => {
				draft.courses_outcomes = action.payload.outcomes;
			});

		default:
			return state;
	}
}

type ResourceAction =
	| { type: 'ADD_RESOURCE'; payload: { type: string } }
	| {
			type: 'ADD_RESOURCE_FROM_SUGGESTION';
			payload: {
				resourceType: string;
				title: string;
				author?: string;
				detail?: string;
				isbn?: string;
			};
	  }
	| { type: 'UPDATE_RESOURCE'; payload: { resources: CoursesResourcesEntity[] } }
	| { type: 'REMOVE_RESOURCE'; payload: { id: string } }
	| { type: 'SET_RESOURCES'; payload: { courses_resources: CoursesResourcesEntity[] } };

function resourceReducer(state: Course, action: ResourceAction): Course {
	switch (action.type) {
		case 'SET_RESOURCES':
			return produce(state, (draft) => {
				draft.courses_resources = action.payload.courses_resources;
			});

		case 'ADD_RESOURCE':
			return produce(state, (draft) => {
				if (!draft.courses_resources) draft.courses_resources = [];
				draft.courses_resources.push({
					course_resources_id: uuidv4(),
					resource: {
						resource_id: uuidv4(),
						resource_type: action.payload.type,
						resource_title: '',
						resource_author: '',
						resource_detail: '',
						resource_isbn: '',
					},
				});
			});

		case 'ADD_RESOURCE_FROM_SUGGESTION':
			return produce(state, (draft) => {
				if (!draft.courses_resources) draft.courses_resources = [];
				draft.courses_resources.push({
					course_resources_id: uuidv4(),
					resource: {
						resource_id: uuidv4(),
						resource_type: action.payload.resourceType,
						resource_title: action.payload.title,
						resource_author: action.payload.author ?? '',
						resource_detail: action.payload.detail ?? '',
						resource_isbn: action.payload.isbn ?? '',
					},
				});
			});

		case 'UPDATE_RESOURCE':
			return produce(state, (draft) => {
				draft.courses_resources = action.payload.resources;
			});

		case 'REMOVE_RESOURCE':
			return produce(state, (draft) => {
				if (draft.courses_resources) {
					draft.courses_resources = draft.courses_resources.filter(
						(resource) => resource.course_resources_id !== action.payload.id
					);
				}
			});

		default:
			return state;
	}
}

type CourseAction =
	| FocusAction
	| OutcomeAction
	| ResourceAction
	| { type: 'UPDATE_RESOURCE_INFO'; payload: string }
	| { type: 'UPDATE_DESCRIPTION'; payload: string }
	| { type: 'UPDATE_PREREQ'; payload: string }
	| { type: 'UPDATE_ADDITIONAL_INFO'; payload: string }
	| { type: 'UPDATE_COURSE_EXTRAS'; payload: string }
	| { type: 'SET_COURSE_DRAFT'; payload: string }
	| { type: 'SET_COURSE_DATA'; payload: { courseData: Course } };

function courseReducer(state: Course, action: CourseAction): Course {
	switch (action.type) {
		// Focus actions
		case 'ADD_FOCUS':
		case 'ADD_FOCUS_WITH_TITLE':
		case 'IMPORT_FOCUS_WITH_SKILLS':
		case 'ADD_SKILL':
		case 'UPDATE_FOCUS':
		case 'SET_FOCUSES':
		case 'REMOVE_FOCUS':
		case 'REMOVE_SKILL':
		case 'REORDER_FOCUSES':
			return focusReducer(state, action);

		// Outcome actions
		case 'ADD_OUTCOME':
		case 'ADD_OUTCOME_WITH_TEXT':
		case 'UPDATE_OUTCOME':
		case 'REMOVE_OUTCOME':
		case 'SET_OUTCOMES':
		case 'REORDER_OUTCOMES':
			return outcomeReducer(state, action);

		// Resource actions
		case 'ADD_RESOURCE':
		case 'ADD_RESOURCE_FROM_SUGGESTION':
		case 'UPDATE_RESOURCE':
		case 'REMOVE_RESOURCE':
		case 'SET_RESOURCES':
			return resourceReducer(state, action);

		// Resource Info
		case 'UPDATE_RESOURCE_INFO':
			return produce(state, (draft) => {
				draft.course_resource_info = action.payload;
			});

		case 'UPDATE_DESCRIPTION':
			return produce(state, (draft) => {
				draft.course_description = action.payload;
			});

		case 'UPDATE_PREREQ':
			return produce(state, (draft) => {
				draft.course_prereq = action.payload;
			});

		case 'UPDATE_COURSE_EXTRAS':
			return produce(state, (draft) => {
				draft.course_extras = action.payload;
			});

		case 'SET_COURSE_DRAFT':
			return produce(state, (draft) => {
				draft.course_draft = action.payload;
			});

		default:
			return state;
	}
}

const CourseEditProvider = ({ ...props }: any) => {
	const { children } = props;
	const { pathname } = useLocation();
	const tabLabels = useTabLabels();

	const isAlignment = pathname.includes('course-alignment');
	const { data: adminUsers } = useGetAllAdmin();
	const { currentUser } = useContext(GlobalContext);
	const { canEditCourse } = usePermissions();

	const [courseData, setCourseData] = useState<any>();
	const [courseDraftData, dispatch] = useReducer(courseReducer, {
		courses_focuses: [],
		courses_outcomes: [],
		course_draft: '',
		course_resource_info: '',
		courses_resources: [],
		course_description: '',
		course_name: '',
		course_id: '',
		course_extras: '',
		course_prereq: '',
		course_discipline: '',
		course_division: '',
		working_copy: '',
		is_ap: false,
		grade: '',
		courses_users: [],
		is_saved_draft: false,
		last_updated: '',
	});
	const { data: userRoleData } = useUserRole();
	const [searchParams] = useSearchParams();
	const isApprovalMode = !!searchParams.get('isApprovalMode');
	const params = useParams<CourseUrlParams>();

	const {
		data: courseInfo,
		isFetchedAfterMount,
		isFetched: isCourseFetched,
	} = useGetCurrentCourses(params.courseId);

	const isFetched = isCourseFetched;
	const [courseSubId, setCourseSubId] = useState('');
	const subIdRef = useRef(courseData?.submission_id ?? '');
	// TODO: look into
	// ?? can I use current user instead?
	const { data: currentUserData } = useCurrentUser();
	const updateDescription = useCallback((editorData: string) => {
		dispatch({ type: 'UPDATE_DESCRIPTION', payload: editorData });
	}, []);

	const editorRefs = useRef(new Map<string, Editor>());

	const updateEditorRef = (key: string, editor: Editor) => {
		editorRefs.current.set(key, editor);
	};

	const canEdit = canEditCourse(courseData?.courses_users);

	const isPendingApproval = useCallback(() => {
		if (courseInfo?.submission) {
			return Object.values(courseInfo.submission).every((status) => status !== SetStatus.APPROVED);
		}
		return false;
	}, [courseInfo?.submission]);

	const updateResourceInfo = useCallback((editorData: string) => {
		dispatch({ type: 'UPDATE_RESOURCE_INFO', payload: editorData });
	}, []);

	const updatePrereq = useCallback((inputVal: string) => {
		dispatch({ type: 'UPDATE_PREREQ', payload: inputVal });
	}, []);

	const updateAdditionalInfo = useCallback((editorData: string) => {
		dispatch({ type: 'UPDATE_COURSE_EXTRAS', payload: editorData });
	}, []);

	const addSubmission = async (variables: { courseId: string }) => {
		const schoolId = import.meta.env.VITE_SCHOOL_ID;

		// Create submission
		const { data: subInsert, error: subError } = await supabase
			.from('submissions')
			.insert({
				course_id: variables.courseId,
				school_id: schoolId,
				admin_approval: SetStatus.WAITING,
				dept_approval: SetStatus.WAITING,
			})
			.select('submission_id')
			.single();

		if (subError) throw subError;

		// Update course with submission_id
		const { error: courseError } = await supabase
			.from('courses')
			.update({ submission_id: subInsert.submission_id })
			.eq('course_id', variables.courseId);

		if (courseError) throw courseError;

		subIdRef.current = subInsert.submission_id;
		setCourseSubId(subInsert.submission_id);

		return { affected_rows: 1 };
	};

	const updateDraft = async (variables: {
		courseId: string;
		courseDraft: string;
		isDraft?: boolean;
	}) =>
		updateDraftFrag({
			variables,
			userRole: userRoleData?.user_role ?? '',
		});

	const queryClient = useQueryClient();

	const setDraftStatus = async (variables: {
		courseId: string;
		subId: string;
		status: string;
		approveAll: boolean;
	}) =>
		setDraftFrag({
			variables,
			draftCourse: courseDraftData,
			userRole: userRoleData?.user_role,
		});

	const { mutate: updateDraftStatusMutate } = useMutation({
		mutationFn: setDraftStatus,
		mutationKey: ['submission-status-mutation'],

		onSuccess: async (_, variables: any) => {
			const { status } = variables;

			const { course_name: courseName } = courseData;

			const recipients = (courseData.courses_users ?? [])
				.map((u: Course['courses_users'][number]) => u.user?.user_email)
				.filter((e: string | undefined): e is string => Boolean(e));

			if (import.meta.env.MODE !== 'development' && recipients.length > 0) {
				const {
					data: { session },
				} = await supabase.auth.getSession();
				if (session?.access_token) {
					void sendCourseNotificationEmail(session.access_token, {
						type: 'course_status_update',
						courseName,
						courseUrl: createEmailSubmissionLink(window.location.href),
						recipients,
					}).catch((err) => console.error('Course status email:', err));
				}
			}

			queryClient.invalidateQueries({
				queryKey: COURSES_LIST_QUERY_KEY,
			});
			queryClient.invalidateQueries({
				queryKey: ['get-current-course'],
			});
			queryClient.invalidateQueries({
				queryKey: ['get-all-subs'],
			});
			queryClient.refetchQueries({ queryKey: ['get-all-alignment-courses'] });

			if (status === 'rejected') {
				showSuccessToast(`Course Draft Changes Requested`);
			} else {
				showSuccessToast(`Course Draft Approved`);
			}
		},

		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const updateDraftStatus = (status: string, approveAll = false) => {
		const subId = courseSubId ?? courseData?.submission_id;
		const { course_id: courseId } = courseData;
		updateDraftStatusMutate({ courseId, subId, status, approveAll });
	};

	const { mutate: plansMutate } = useMutation({
		mutationFn: updateLessonPlans,
		mutationKey: ['save-course-lesson'],
		onSuccess: () => {
			showSuccessToast(`${tabLabels.lessonPlans} Saved`);
		},
		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const updatePlans = (plans: LessonSection[], courseId: string) => {
		plansMutate({ plans, courseId });
	};

	const { mutate: saveDraftMutate } = useMutation({
		mutationFn: updateDraft,
		mutationKey: ['save-course-draft'],

		onSuccess: () => {
			showSuccessToast('Course Draft Saved');

			queryClient.invalidateQueries({
				queryKey: COURSES_LIST_QUERY_KEY,
			});
			const previousCourseData = queryClient.getQueryData(['get-current-course']) as
				| Partial<Course>
				| undefined;

			if (previousCourseData) {
				previousCourseData.is_saved_draft = true;
				// update query cache
				queryClient.setQueryData(['get-current-course'], previousCourseData);
			}
		},

		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const { mutate: draftMutate } = useMutation({
		mutationFn: updateDraft,
		mutationKey: ['submit-course'],

		onSuccess: async (results: any) => {
			queryClient.invalidateQueries({
				queryKey: COURSES_LIST_QUERY_KEY,
			});

			const previousCourseData = queryClient.getQueryData(['get-current-course']) as
				| Partial<Course>
				| undefined;

			if (previousCourseData) {
				previousCourseData.is_saved_draft = false;
				previousCourseData.submission = {
					admin_approval: SetStatus.WAITING,
					dept_approval: SetStatus.WAITING,
				};
				// update query cache
				queryClient.setQueryData(['get-current-course'], previousCourseData);
			}

			const isSubmitted = results?.update_courses_by_pk?.submission_id;
			subIdRef.current = isSubmitted;
			setCourseSubId(isSubmitted);
			if (isSubmitted) {
				showSuccessToast('Course Draft Resubmitted');
			} else {
				showSuccessToast('Course Draft Submitted');
			}

			const { course_name: courseName } = courseData;
			const firstName = currentUser?.user_first ?? '';
			const lastName = currentUser?.user_last ?? '';
			const submitterName = `${firstName} ${lastName}`;

			const courseUrl = createEmailSubmissionLink(window.location.href);
			if (import.meta.env.MODE !== 'development') {
				const recipients: string[] = [];
				const deptEmail = currentUser?.dept_chair?.user_email;
				if (deptEmail) {
					recipients.push(deptEmail);
				}
				const adminEmails = (adminUsers ?? [])
					.map((user) => user.user_email)
					.filter(
						(email): email is string =>
							Boolean(email) &&
							email !== currentUser?.dept_chair?.user_email &&
							email !== currentUser?.user_email
					);
				recipients.push(...adminEmails);

				if (recipients.length > 0) {
					const {
						data: { session },
					} = await supabase.auth.getSession();
					if (session?.access_token) {
						void sendCourseNotificationEmail(session.access_token, {
							type: 'course_submission',
							courseName,
							courseUrl,
							submitterName,
							recipients,
						}).catch((err) => console.error('Course submission email:', err));
					}
				}
			}

			queryClient.invalidateQueries({
				queryKey: ['get-all-subs'],
			});
		},

		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const updateSubStatus = async (variables: { courseId: string }) =>
		updateSubStatusFrag({
			variables,
			userRole: userRoleData?.user_role,
		});

	const getSubId = useCallback(
		async (variables: { courseId: string }) =>
			existingSub({
				variables,
				userRole: userRoleData?.user_role,
			}),
		[userRoleData?.user_role]
	);

	const { mutate: subStatusMutate } = useMutation({
		mutationFn: updateSubStatus,

		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['get-all-subs'],
			});
			queryClient.invalidateQueries({
				queryKey: ['get-current-course'],
			});
		},

		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const { mutate: subMutate } = useMutation({
		mutationFn: addSubmission,

		onError: () => {
			showErrorToast('Oh no, something went wrong... Please try again.');
		},
	});

	const saveDraft = useCallback(async () => {
		const clone = { ...courseDraftData };
		const { course_id: courseId } = courseData;

		saveDraftMutate({ courseId, courseDraft: JSON.stringify(clone), isDraft: true });
	}, [courseDraftData, saveDraftMutate, courseData]);

	const submitCourse = useCallback(async () => {
		const { course_id: courseId } = courseData;
		const hasSubmission = await getSubId({ courseId });

		if (hasSubmission) {
			subStatusMutate({ courseId });
			const clone = { ...courseDraftData } as { course_draft?: any };
			delete clone.course_draft;
			draftMutate({ courseId, courseDraft: JSON.stringify(clone) });
		} else {
			const clone = { ...courseDraftData } as { course_draft?: any };
			delete clone.course_draft;
			draftMutate({ courseId, courseDraft: JSON.stringify(clone) });
			subMutate({ courseId });
		}
	}, [courseData, subMutate, courseDraftData, draftMutate, subStatusMutate, getSubId]);

	const updateOutcomeOrder = (outcomeArr: CoursesOutcomesEntity[]) => {
		// const { destination, source } = result;
		// if (!destination) {
		// 	return;
		// }

		// if (destination.droppableId === source.droppableId && destination.index === source.index) {
		// 	return;
		// }

		// const sortedArr = outcomeArr.sort(
		// 	(a: CoursesOutcomesEntity, b: CoursesOutcomesEntity) =>
		// 		a.outcome.outcome_order - b.outcome.outcome_order
		// );

		// const match = sortedArr.splice(source.index, 1)[0];
		// sortedArr.splice(destination.index, 0, match);

		// const updatedOutcomes = outcomeArr.map((outcome: CoursesOutcomesEntity, index: number) => {
		// 	outcome.outcome.outcome_order = index;
		// 	return outcome;
		// });
		dispatch({ type: 'REORDER_OUTCOMES', payload: { outcomes: outcomeArr } });
	};

	const updateOutcome = useCallback((data: any) => {
		dispatch({ type: 'UPDATE_OUTCOME', payload: data });
	}, []);

	const updateFocusOrder = (focusArr: CourseFocusEntity[]) => {
		dispatch({ type: 'REORDER_FOCUSES', payload: { focuses: focusArr } });
	};

	const removeOutcome = (id: string) => {
		dispatch({ type: 'REMOVE_OUTCOME', payload: { id } });
	};

	const addOutcome = () => {
		dispatch({ type: 'ADD_OUTCOME' });
	};

	const addOutcomeWithText = (text: string) => {
		dispatch({ type: 'ADD_OUTCOME_WITH_TEXT', payload: { text } });
	};

	const removeResource = (id: string) => {
		dispatch({ type: 'REMOVE_RESOURCE', payload: { id } });
	};

	const addResource = (type: string) => {
		dispatch({ type: 'ADD_RESOURCE', payload: { type } });
	};

	const addResourceFromSuggestion = (payload: {
		resourceType: string;
		title: string;
		author?: string;
		detail?: string;
		isbn?: string;
	}) => {
		dispatch({ type: 'ADD_RESOURCE_FROM_SUGGESTION', payload });
	};

	const updateResources = (resources: CoursesResourcesEntity[]) => {
		dispatch({ type: 'UPDATE_RESOURCE', payload: { resources } });
	};

	const addFocus = useCallback(() => {
		dispatch({ type: 'ADD_FOCUS' });
	}, []);

	const addFocusWithTitle = useCallback((title: string) => {
		dispatch({ type: 'ADD_FOCUS_WITH_TITLE', payload: { title } });
	}, []);

	const importFocusFromPull = useCallback((payload: PulledFocusImport) => {
		dispatch({ type: 'IMPORT_FOCUS_WITH_SKILLS', payload });
	}, []);

	const removeFocus = (id: string) => {
		dispatch({ type: 'REMOVE_FOCUS', payload: { focusId: id } });
	};

	const updateFocus = (currentFocus: CourseFocusEntity[]) => {
		dispatch({ type: 'UPDATE_FOCUS', payload: { courses_focuses: currentFocus } });
	};

	const addSkill = useCallback((focusId: string, skillType: string) => {
		dispatch({ type: 'ADD_SKILL', payload: { focusId, skillType } });
	}, []);

	const removeSkill = (data: { focusSkillId: string; courseFocusId: string }) => {
		dispatch({ type: 'REMOVE_SKILL', payload: data });
	};

	const updateCourseReducer = (state: any, action: any) => {
		switch (action) {
			case EditorType.DESCRIPTION:
				return updateDescription(state);
			case EditorType.EXTRAINFO:
				return updateAdditionalInfo(state);
			case EditorType.OUTCOME:
				return updateOutcome(state);
			case EditorType.RESOURCES:
				return updateResourceInfo(state);
			default:
				return state;
		}
	};

	const lessonPlans: any = [
		{
			id: uuidv4(),
			title: '',
			introduction: '',
			lessons: '',
			objectives: '',
			resources: '',
			enrichment: '',
			supportMaterial: '',
			bellringer: '',
			hasAdvanced: true,
		},
	];

	const { setLessonPlans } = useCourseLessonsStore();
	useEffect(() => {
		subIdRef.current = courseData?.submission_id ?? '';
		setCourseData(courseInfo);
		setLessonPlans((courseData?.course_lesson_plans as LessonSection[] | null) ?? []);
	}, [courseInfo, courseData, setLessonPlans]);

	useEffect(() => {
		setCourseData(courseInfo);
	}, [courseInfo, courseData]);

	useEffect(() => {
		if (courseInfo) {
			const courseVersion =
				courseInfo.is_saved_draft && !isApprovalMode
					? courseInfo.working_copy
					: courseInfo.course_draft;
			const parsedCourseVersion = JSON.parse(courseVersion ?? '{}');
			dispatch({
				type: 'UPDATE_DESCRIPTION',
				payload: parsedCourseVersion?.course_description,
			});
			dispatch({
				type: 'UPDATE_PREREQ',
				payload: parsedCourseVersion?.course_prereq,
			});
			dispatch({
				type: 'UPDATE_COURSE_EXTRAS',
				payload: parsedCourseVersion?.course_extras,
			});
			dispatch({
				type: 'SET_OUTCOMES',
				payload: { courses_outcomes: parsedCourseVersion?.courses_outcomes ?? [] },
			});
			dispatch({
				type: 'SET_FOCUSES',
				payload: { courses_focuses: parsedCourseVersion?.courses_focuses ?? [] },
			});
			dispatch({
				type: 'SET_RESOURCES',
				payload: { courses_resources: parsedCourseVersion?.courses_resources ?? [] },
			});
			dispatch({
				type: 'UPDATE_RESOURCE_INFO',
				payload: parsedCourseVersion?.course_resource_info,
			});
		}
	}, [courseInfo, isApprovalMode]);

	return (
		<CourseEditContext.Provider
			value={{
				addFocus,
				addFocusWithTitle,
				importFocusFromPull,
				addOutcome,
				addOutcomeWithText,
				addResource,
				addResourceFromSuggestion,
				addSkill,
				canEdit,
				courseData,
				courseDraftData,
				currentUserData,
				editorRefs,
				isAlignment,
				isApprovalMode,
				isFetched,
				isFetchedAfterMount,
				isPendingApproval,
				lessonPlans,
				removeFocus,
				removeOutcome,
				removeResource,
				removeSkill,
				saveDraft,
				submitCourse,
				updateAdditionalInfo,
				updateCourseReducer,
				updateDescription,
				updateDraftStatus,
				updateEditorRef,
				updateFocus,
				updateFocusOrder,
				updateOutcome,
				updateOutcomeOrder,
				updatePrereq,
				updateResourceInfo,
				updateResources,
				updatePlans,
			}}
		>
			<Fragment key={courseData?.course_id}>{children}</Fragment>
		</CourseEditContext.Provider>
	);
};

export default CourseEditContext;

export { CourseEditProvider };
